import React, {useRef, useEffect} from 'react';

import Base from '@/templates/Base/Base';

import Container from '@Themes/natucare/components/Container/Container';
import Article from '@Themes/natucare/modules/Article/Article';
import ArticleHero from '@Themes/natucare/modules/ArticleHero/ArticleHero';
import SimilarArticles from '@Themes/natucare/modules/SimilarArticles/SimilarArticles';
import Authorship from '@Themes/natucare/modules/Authorship/Authorship';
import Cta from '@Themes/natucare/modules/Cta/Cta';
import MediabarBlog from '@Themes/natucare/modules/MediabarBlog/MediabarBlog';

import Seo from '@/components/Seo/Seo';
import ScoreApi from '@/components/ScoreApi/ScoreApi';

import {useSiteMetadata} from '@/hooks/useSiteMetadata';
import {useYoutubeType} from '@/hooks/useYoutubeType';
import {useCalculatorType} from "@/hooks/useCalculatorType";
import {useSelectionTooltip} from '@/hooks/useSelectionTooltip';

import {getStructuredData} from '@/utils/getStrucutredData';
import {getBreadcrumbs} from '@/templates/Blog/utils/getBreadcrumbs';
import {getAuthorsArray} from '@/templates/Blog/utils/getAuthorsArray';
import {getActivityDates} from '@/templates/Blog/utils/getActivityDates';
import {getReadingTime} from '@/templates/Blog/utils/getReadingTime';
import {parsePosts} from '@/templates/Blog/utils/parsePosts';
import {parseContent} from '@/templates/Blog/utils/parseContent/parseContent';

import DATA from '@/data/blog-post.data';

import './BlogPost.scss';

const BlogPost = ({data, postsModel, pageContext, hrefLang}) => {
  const articleRef = useRef();
  const blogPostWrapperRef = useRef();

  const siteMetadata = useSiteMetadata();
  useSelectionTooltip(blogPostWrapperRef);
  useYoutubeType(articleRef);
  useCalculatorType({articleRef, data: pageContext.calculatorLists});

  const isPostOnBlogListPage = !data?.category;

  const {locale: contextLocale, localeSlug, content, cleanContent, headerData, footerData, tocLinks, faqList, votes, hasNoindex} = pageContext;

  const locale = localeSlug ? `/${localeSlug}` : '';
  const siteUrl = `${siteMetadata.siteUrl}${locale}`;
  const categorySlug = isPostOnBlogListPage ? `/${data?.slug}` : `/${data?.category.slug}`;
  const articleSlug = isPostOnBlogListPage ? categorySlug : `${categorySlug}/${data.slug}`;
  const articleUrl = `${siteUrl}${articleSlug}`;

  const rolesOrder = ['author', 'reviewedBy', 'editedBy', 'factChecker'];
  
  const editorialTeam = getAuthorsArray(data, rolesOrder, contextLocale);

  const activityRolesOrder = ['reviewedBy', 'updatedBy', 'factChecker'];
  const activityDates = getActivityDates(data, activityRolesOrder, contextLocale);

  const posts = parsePosts({postsModel, post: data});

  const ogImage = data.seo.image?.url || data.featuredImage?.url;

  const timeRequired = getReadingTime(content);

  const parsedContent = parseContent(content, pageContext);

  const breadcrumbs = getBreadcrumbs(
    [
      {
        label: DATA[contextLocale].breadcrumbs.home,
        schemaLabel: '❤️',
        href: locale,
        schemaHref: siteUrl
      },
      ...(!isPostOnBlogListPage ? [{
        label: data.category.shortHeading || data.category.heading,
        href: `${locale}${categorySlug}`,
        schemaHref: `${siteUrl}${categorySlug}`
      }] : []),
      {
        label: data.shortHeading || data.heading,
        schemaHref: `${siteUrl}${articleSlug}`
      }
    ]
  );

  
  const structuredData = getStructuredData({
    data,
    breadcrumbs,
    articleUrl,
    siteUrl,
    cleanContent,
    votes,
    faqList,
    timeRequired
  });

  const handleSubscribeNewsletter = async (email) => {
    try {
      const response = await fetch('/api/subscribe-newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({email})
      });

      const data = await response.json();

      return {
        isSubscribed: !data.errorCode,
        isEmailTaken: data.errorCode === 'EMAIL_ALREADY_TAKEN'
      };

    } catch (error) {
      console.error(error);
    }
  };


  useEffect(() => {
    let isCameFromSem = false;

    const params = ['gad_source', 'gclid', 'gclsrc', 'wbraid', 'gbraid', 'fbclid', 'twclid', 'ttclid' ];
    const excludeParams = ['utm_medium', 'utm_source', 'utm_campaign'];

    const urlSearchParams = new URLSearchParams(window.location.search);

    params.forEach(param => {
      if (urlSearchParams.has(param)) {
        isCameFromSem = true;
      }
    })

    if (isCameFromSem) {
      const links = articleRef.current.querySelectorAll('a');
      [].slice.call(links).forEach(el => {
        const url = new URL(el.href);
        if(url.search) {
          let ifRemoveParam = false;
          const params = new URLSearchParams(url.search);

          excludeParams.forEach(param => {
            if (params.has(param)) { 
              params.delete(param);
              ifRemoveParam = true;
            }
          });

          if (ifRemoveParam) {
            const newUrl = `${url.protocol}//${url.host}${url.pathname}${params.size > 0 ? `?${params.toString()}` : ''}${url.hash}`;
            el.href = newUrl;
          }
        }
      })
    }

    const rankingLinks = articleRef.current.querySelectorAll('.rankingProductTitle a');
    const horizontalLinks = articleRef.current.querySelectorAll('.horizontalProduct a');

    const allLinks = [].slice.call(rankingLinks).concat([].slice.call(horizontalLinks));

    if (allLinks && allLinks.length > 0) {
      [].slice.call(allLinks).forEach(el => {
        el.addEventListener('click', async e => {
          e.preventDefault(); 
    
          const targetUrl = e.currentTarget.href;
    
          const isInternal = (
            targetUrl.indexOf('shop.natu.care') !== -1 ||
            targetUrl.indexOf('natu.shop') !== -1
          );
          
          const pushToDataLayer = (eventType) => {
            return new Promise((resolve) => {
              if (window.dataLayer) {
                window.dataLayer.push({
                  event: eventType
                });
              }
              resolve();
            });
          };
    
          await pushToDataLayer(isInternal ? 'internal_tile_click' : 'external_tile_click');
    
          window.location.href = targetUrl;
        });
      });
    }
    
  });
  
  return (
    <Base  headerData={headerData} footerData={footerData} isBlogPostTemplate={true} ref={articleRef} isKlaviyoPl={contextLocale === 'pl'}>
      <Seo
        hasNoindex={hasNoindex}
        lang={contextLocale}
        title={data.seo.title}
        description={data.seo.description}
        date={data.updatedAtCustom}
        ogImage={ogImage}
        ogType='article'
        hrefLang={hrefLang}
        vwo={data.options?.strapi_json_value.indexOf('vwo') !== -1}
        meta={[
          {
            property: 'article:published_time',
            content: data.metaPublishedTime
          },
          {
            property: 'article:modified_time',
            content: data.metaUpdatedTime
          },
          {
            property: 'entryID',
            content: data.strapi_id
          }
        ]}
        {...structuredData}
      />
      <div className='blogPostWrapper' ref={blogPostWrapperRef}>
        <ArticleHero
          breadcrumbs={breadcrumbs}
          heading={data.heading}
          description={data.shortContent}
          featuredImage={{
            src: data.featuredImage?.url,
            alt: data.heading
          }}
          editorialTeam={editorialTeam}
          activityDates={activityDates}
          readingTime={{value: timeRequired, unit: 'min'}}
          badges={DATA[contextLocale].hero.badges}
        />
        <MediabarBlog {...DATA[contextLocale].mediabar} />
        <Container classname='blogPost'>
          <div
            className='blogPost_body'
            ref={articleRef}>
            {parsedContent && <Article
              html={parsedContent}
              onSubscribe={handleSubscribeNewsletter}/>}
          </div>
          <ScoreApi {...votes} locale={contextLocale} />
        </Container>
        {editorialTeam?.length > 0 &&
          <Authorship
            heading={DATA[contextLocale].authorship.heading}
            link={DATA[contextLocale].authorship.link}
            items={editorialTeam}/>}
        <Cta
          heading={DATA[contextLocale].cta.heading}
          description={DATA[contextLocale].cta.description}
          button={DATA[contextLocale].cta.button}
          picture={{
            xs: {src: '/images/blog-cta-picture-xs.jpg'},
            md: {src: '/images/blog-cta-picture-md.jpg'},
            alt: DATA[contextLocale].cta.picture.alt
          }}
        />
        {posts?.length > 0 && <SimilarArticles
          heading={isPostOnBlogListPage ? DATA[contextLocale].similarArticles.headingBlogList : DATA[contextLocale].similarArticles.headingBlogPost}
          items={posts}/>}
      </div>
    </Base>
  );
};

export default BlogPost;
